import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  ClockIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { getHoursAsync, updateHourAsync } from "../../../slices/hour";
import { dateToString, UserTitle, dispatchWithToast } from "../../Utils";
import { ApproveHourDialog } from "../hours/approveHourDialog";
import { HourEditDialog } from "../hours/hour_edit_dialog";
import { HoursBulkReassignDialog } from "../hours/hours_bulk_reassign_dialog";
import { projectActivityLine } from "../hours/projectActivityDisplay";
import { useCanBulkReassignHours } from "../../../routes/roleOnly";

/** Group label: kun project_activity (prosjektlinje), ikke legacy activity. */
function entryActivityGroupLabel(entry, noActivityLabel) {
  const line = projectActivityLine(entry.project_activity);
  if (line) return line;
  if (entry.project_activity_id != null) return `#${entry.project_activity_id}`;
  return noActivityLabel;
}

function groupByActivity(entries, noActivityLabel) {
  return (entries || []).reduce((acc, entry) => {
    const activityKey = entryActivityGroupLabel(entry, noActivityLabel);
    if (!acc[activityKey]) acc[activityKey] = [];
    acc[activityKey].push(entry);
    return acc;
  }, {});
}

function sumHours(entries) {
  return (entries || []).reduce(
    (sum, entry) => sum + (parseFloat(entry.time) || 0),
    0
  );
}

export const ProjectTimeList = ({
  project,
  canManageProjectTime = false,
  restrictHoursToCurrentUserId = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canBulkReassign = useCanBulkReassignHours();
  const [expandedApproved, setExpandedApproved] = useState(new Set());
  const [expandedExcluded, setExpandedExcluded] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [processDialogHour, setProcessDialogHour] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editHourId, setEditHourId] = useState(null);
  const [selectedHourIds, setSelectedHourIds] = useState(new Set());
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const hourFiltersForProject = useCallback(() => {
    const f = [{ name: "PROJECT", value: project?.id }];
    if (
      restrictHoursToCurrentUserId != null &&
      restrictHoursToCurrentUserId !== ""
    ) {
      f.push({ name: "USER", value: restrictHoursToCurrentUserId });
    }
    return f;
  }, [project?.id, restrictHoursToCurrentUserId]);

  const [controller, setController] = useState(() => ({
    query: "",
    page: 1,
    per_page: 100,
    sortBy: "date",
    sort: "desc",
    filters: (() => {
      const f = [{ name: "PROJECT", value: project?.id }];
      if (
        restrictHoursToCurrentUserId != null &&
        restrictHoursToCurrentUserId !== ""
      ) {
        f.push({ name: "USER", value: restrictHoursToCurrentUserId });
      }
      return f;
    })(),
    date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    date_to: new Date().toISOString().split("T")[0],
    fields:
      "id,time,date,description,process_note,user,project,project_activity,project_activity_id,status,effective_hourly_rate,effective_hour_cost_rate,approved,not_approved,processed",
  }));

  const itemState = useSelector((store) => store.hours);
  const { data: timeEntries, isLoading: loading } = itemState;

  useEffect(() => {
    if (project?.id) {
      dispatch(getHoursAsync(controller));
    }
  }, [dispatch, controller, project?.id]);

  useEffect(() => {
    setController((prev) => ({
      ...prev,
      filters: hourFiltersForProject(),
    }));
  }, [project?.id, hourFiltersForProject]);

  const noActivity = t("project.time_registration.no_activity");

  const { approvedEntries, excludedEntries, groupedApproved, groupedExcluded } =
    useMemo(() => {
      const list = timeEntries || [];
      const approved = list.filter((e) => e.approved);
      const excluded = list.filter((e) => !e.approved);
      return {
        approvedEntries: approved,
        excludedEntries: excluded,
        groupedApproved: groupByActivity(approved, noActivity),
        groupedExcluded: groupByActivity(excluded, noActivity),
      };
    }, [timeEntries, noActivity]);

  const approvedTotals = useMemo(
    () =>
      Object.keys(groupedApproved).reduce((acc, activity) => {
        acc[activity] = sumHours(groupedApproved[activity]);
        return acc;
      }, {}),
    [groupedApproved]
  );

  const excludedTotals = useMemo(
    () =>
      Object.keys(groupedExcluded).reduce((acc, activity) => {
        acc[activity] = sumHours(groupedExcluded[activity]);
        return acc;
      }, {}),
    [groupedExcluded]
  );

  const totalApprovedHours = sumHours(approvedEntries);
  const totalExcludedHours = sumHours(excludedEntries);

  const toggleExpanded = (setFn, activity) => {
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(activity)) next.delete(activity);
      else next.add(activity);
      return next;
    });
  };

  const processHourQuick = useCallback(
    (hour, approved) => {
      dispatchWithToast(
        dispatch(
          updateHourAsync({
            id: hour.id,
            process: true,
            approved,
            not_approved: !approved,
            process_note: hour.process_note || "",
          })
        ),
        () => {}
      );
    },
    [dispatch]
  );

  const openHourEditDialog = useCallback((entry) => {
    if (!canManageProjectTime || entry?.id == null) return;
    setEditHourId(String(entry.id));
    setEditDialogOpen(true);
  }, [canManageProjectTime]);

  const toggleHourSelected = useCallback((entryId) => {
    setSelectedHourIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  }, []);

  const closeHourEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditHourId(null);
  }, []);

  const refreshHours = useCallback(() => {
    if (project?.id) {
      dispatch(getHoursAsync(controller));
    }
  }, [dispatch, controller, project?.id]);

  const handleHourSaved = useCallback(() => {
    refreshHours();
    closeHourEditDialog();
  }, [refreshHours, closeHourEditDialog]);

  const handleHourDeleted = useCallback(() => {
    refreshHours();
    closeHourEditDialog();
  }, [refreshHours, closeHourEditDialog]);

  const approvalBadge = (entry) => {
    if (entry.approved) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
          {t("project.time_registration.badge_approved")}
        </span>
      );
    }
    if (entry.not_approved) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          {t("project.time_registration.badge_rejected")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
        {t("project.time_registration.badge_pending")}
      </span>
    );
  };

  const renderEntryRow = (entry) => (
    <div
      key={entry.id}
      className={`flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 p-4${
        canManageProjectTime ? " cursor-pointer hover:bg-gray-50" : ""
      }`}
      onClick={canManageProjectTime ? () => openHourEditDialog(entry) : undefined}
      role={canManageProjectTime ? "button" : undefined}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
        {canManageProjectTime && canBulkReassign && (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            checked={selectedHourIds.has(entry.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleHourSelected(entry.id)}
            aria-label={t("project.time_registration.select_hour")}
          />
        )}
        <div className="flex items-center text-sm text-gray-600">
          <CalendarIcon className="mr-1 h-4 w-4 shrink-0" />
          {dateToString(entry.date)}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <UsersIcon className="mr-1 h-4 w-4 shrink-0" />
          <UserTitle user={entry.user} />
        </div>
        <div className="text-sm text-gray-700">{entry.description}</div>
        {approvalBadge(entry)}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {canManageProjectTime && (
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {!entry.approved && (
              <>
                <button
                  type="button"
                  title={t("project.time_registration.approve")}
                  className="rounded p-1 text-green-600 hover:bg-green-50"
                  onClick={() => processHourQuick(entry, true)}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  title={t("project.time_registration.reject")}
                  className="rounded p-1 text-red-600 hover:bg-red-50"
                  onClick={() => processHourQuick(entry, false)}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              type="button"
              title={t("project.time_registration.process_dialog")}
              className="rounded p-1 text-gray-600 hover:bg-gray-100"
              onClick={() => setProcessDialogHour(entry)}
            >
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              title={t("project.time_registration.open_hour")}
              className="rounded p-1 text-indigo-600 hover:bg-indigo-50"
              onClick={() => openHourEditDialog(entry)}
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          </div>
        )}
        <span className="font-medium text-gray-900">
          {parseFloat(entry.time) || 0}h
        </span>
        <ClockIcon className="h-4 w-4 text-gray-400" />
      </div>
    </div>
  );

  const renderActivitySection = (grouped, totals, expandedSet, setExpandedSet) => {
    const keys = Object.keys(grouped);
    if (keys.length === 0) return null;

    return keys.map((activity) => (
      <div key={activity} className="rounded-lg border border-gray-200">
        <div
          className="flex cursor-pointer items-center justify-between bg-gray-50 p-4 hover:bg-gray-100"
          onClick={() => toggleExpanded(setExpandedSet, activity)}
        >
          <div className="flex items-center">
            {expandedSet.has(activity) ? (
              <ChevronDownIcon className="mr-2 h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRightIcon className="mr-2 h-5 w-5 text-gray-400" />
            )}
            <span className="font-medium text-gray-900">{activity}</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              {grouped[activity].length}{" "}
              {t("project.time_registration.registrations")}
            </span>
            <span className="font-medium text-indigo-600">
              {totals[activity]}h
            </span>
          </div>
        </div>
        {expandedSet.has(activity) && (
          <div>{grouped[activity].map((entry) => renderEntryRow(entry))}</div>
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
        <span className="ml-2 text-gray-600">
          {t("project.time_registration.loading")}
        </span>
      </div>
    );
  }

  const hasAny = (timeEntries || []).length > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t("project.time_registration.list_title", {
              title: project?.displayTitle || "",
            })}
          </h3>
          <span className="text-sm text-gray-600">
            {t("project.time_registration.total_approved_hours")}:{" "}
            <strong>{totalApprovedHours.toFixed(1)}h</strong>
            {totalExcludedHours > 0 && (
              <>
                {" "}
                · {t("project.time_registration.total_excluded_hours")}:{" "}
                <strong>{totalExcludedHours.toFixed(1)}h</strong>
              </>
            )}
          </span>
        </div>

        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        >
          <FunnelIcon className="mr-2 h-4 w-4" />
          {t("project.time_registration.filter")}
        </button>
      </div>

        {canManageProjectTime && canBulkReassign && selectedHourIds.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3">
            <span className="text-sm text-indigo-900">
              {t("Valgt")}: {selectedHourIds.size}
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                onClick={() => setBulkReassignOpen(true)}
              >
                {t("project.time_registration.bulk_reassign")}
              </button>
              <button
                type="button"
                className="rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-sm text-indigo-800 hover:bg-indigo-100"
                onClick={() => setSelectedHourIds(new Set())}
              >
                {t("Nullstill")}
              </button>
            </div>
          </div>
        )}

        {showFilters && (
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("project.time_registration.date_from")}
              </label>
              <input
                type="date"
                value={controller.date_from}
                onChange={(e) =>
                  setController((prev) => ({
                    ...prev,
                    date_from: e.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("project.time_registration.date_to")}
              </label>
              <input
                type="date"
                value={controller.date_to}
                onChange={(e) =>
                  setController((prev) => ({ ...prev, date_to: e.target.value }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      )}

      {!hasAny ? (
        <div className="py-8 text-center text-gray-500">
          <ClockIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p>{t("project.time_registration.empty")}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <div className="rounded-md border border-green-200 bg-green-50/80 px-4 py-3">
              <h4 className="text-sm font-semibold text-green-900">
                {t("project.time_registration.approved_section")}
              </h4>
              <p className="mt-1 text-xs text-green-800">
                {t("project.time_registration.approved_section_hint")}
              </p>
            </div>
            {Object.keys(groupedApproved).length === 0 ? (
              <p className="text-sm text-gray-500">
                {t("project.time_registration.no_approved_in_range")}
              </p>
            ) : (
              renderActivitySection(
                groupedApproved,
                approvedTotals,
                expandedApproved,
                setExpandedApproved
              )
            )}
          </div>

          {excludedEntries.length > 0 && (
            <div className="space-y-2 pt-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                <h4 className="text-sm font-semibold text-amber-900">
                  {t("project.time_registration.excluded_section")}
                </h4>
                <p className="mt-1 text-xs text-amber-800">
                  {t("project.time_registration.excluded_section_hint")}
                </p>
              </div>
              {renderActivitySection(
                groupedExcluded,
                excludedTotals,
                expandedExcluded,
                setExpandedExcluded
              )}
            </div>
          )}

          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {t("project.time_registration.summary_approved_label")}
              </span>
              <span className="text-lg font-bold text-indigo-900">
                {totalApprovedHours.toFixed(1)}h
              </span>
            </div>
            {totalExcludedHours > 0 && (
              <p className="mt-2 text-sm text-indigo-800">
                {t("project.time_registration.summary_excluded_note", {
                  hours: totalExcludedHours.toFixed(1),
                })}
              </p>
            )}
          </div>
        </>
      )}

      <ApproveHourDialog
        key={processDialogHour?.id || "closed"}
        hour={processDialogHour}
        open={Boolean(processDialogHour)}
        onClose={() => setProcessDialogHour(null)}
        onRefresh={() => setProcessDialogHour(null)}
      />

      <HourEditDialog
        open={editDialogOpen}
        hourId={editHourId}
        prefill={null}
        onClose={closeHourEditDialog}
        onSaved={handleHourSaved}
        onDeleted={handleHourDeleted}
      />

      <HoursBulkReassignDialog
        open={bulkReassignOpen}
        onClose={() => setBulkReassignOpen(false)}
        hourIds={Array.from(selectedHourIds)}
        initialProject={project || null}
        onDone={() => {
          setSelectedHourIds(new Set());
          refreshHours();
        }}
      />
    </div>
  );
};
